import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Auth from './auth/auth';
import './DataTable.css';

const DiwaliReporting = () => {
    const baseurl = process.env.REACT_APP_API_URL;

    const [dashboardData, setDashboardData] = useState({
        monthlyEarnings: 0,
        annualEarnings: 0,
        taskCompletion: 0,
        pendingRequests: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchReporting = useCallback(async () => {
        const token = localStorage.getItem('token');

        if (!token) {
            setError('Authentication required. Redirecting to login...');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const response = await axios.get(`${baseurl}/reporting`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const data = response.data;

            setDashboardData({
                monthlyEarnings: data.monthly_earnings || data.monthlyEarnings || 40000,
                annualEarnings: data.annual_earnings || data.annualEarnings || 215000,
                taskCompletion: data.task_completion || data.taskCompletion || 50,
                pendingRequests: data.pending_requests || data.pendingRequests || 18,
            });
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            const message =
                err.response?.data?.message ||
                'Failed to load dashboard data. Please try again later.';
            setError(message);

            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
        }
    }, [baseurl]);

    useEffect(() => {
        fetchReporting();
    }, [fetchReporting]);

    return (
        <Container fluid className="reporting-container">
            <Auth />
            
            {/* Header Box */}
            <div className="reporting-header-box">
                <div className="reporting-title-wrap">
                    <h2 className="reporting-title">
                        <span>🎉</span> Diwali Bonus Reporting
                    </h2>
                    <p className="reporting-subtitle">Special campaign targets, incentive qualifications, and annual summaries</p>
                </div>
                <div className="reporting-actions-wrap">
                    <button
                        className="btn-filter-apply"
                        onClick={fetchReporting}
                        disabled={loading}
                    >
                        <span>🔄</span> {loading ? 'Refreshing...' : 'Refresh Metrics'}
                    </button>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {loading && !error && (
                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Retrieving Diwali campaign statistics...</p>
                </div>
            )}

            {!loading && (
                <>
                    <Row className="g-4 mb-4">
                        <Col xs={12} sm={6} xl={3}>
                            <Card className="executive-kpi-card stat-accent-indigo">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <span className="kpi-label">Monthly Target Earnings</span>
                                        <div className="kpi-icon-pill">💰</div>
                                    </div>
                                    <div className="kpi-value">₹{dashboardData.monthlyEarnings.toLocaleString()}</div>
                                    <div className="kpi-meta">Calculated milestone</div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card className="executive-kpi-card stat-accent-emerald">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <span className="kpi-label">Annual Projected Bonus</span>
                                        <div className="kpi-icon-pill">🎁</div>
                                    </div>
                                    <div className="kpi-value">₹{dashboardData.annualEarnings.toLocaleString()}</div>
                                    <div className="kpi-meta">Annual achievement</div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card className="executive-kpi-card stat-accent-cyan">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <span className="kpi-label">Campaign Completion</span>
                                        <div className="kpi-icon-pill">🎯</div>
                                    </div>
                                    <div className="kpi-value">{dashboardData.taskCompletion}%</div>
                                    <div className="progress mt-2" style={{ height: '6px' }}>
                                        <div
                                            className="progress-bar bg-info"
                                            role="progressbar"
                                            style={{ width: `${dashboardData.taskCompletion}%` }}
                                            aria-valuenow={dashboardData.taskCompletion}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card className="executive-kpi-card stat-accent-amber">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <span className="kpi-label">Pending Verifications</span>
                                        <div className="kpi-icon-pill">⏳</div>
                                    </div>
                                    <div className="kpi-value">{dashboardData.pendingRequests}</div>
                                    <div className="kpi-meta text-warning">Awaiting upline check</div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Campaign Info Card */}
                    <div className="filter-panel-card">
                        <div className="filter-card-title">
                            <span>💡</span> Campaign Information & Qualifications
                        </div>
                        <p className="text-secondary mb-3">
                            Diwali Bonus points are computed based on confirmed sales, member promotions, and direct team completions recorded within the active quarterly sprint cycle.
                        </p>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <div className="detail-field-box">
                                    <span className="detail-field-label">Current Cycle</span>
                                    <span className="detail-field-val">Q3 Festive Sprint 2026</span>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="detail-field-box">
                                    <span className="detail-field-label">Audit Status</span>
                                    <span className="detail-field-val text-success">Verified Active</span>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="detail-field-box">
                                    <span className="detail-field-label">Disbursement Portal</span>
                                    <span className="detail-field-val">Direct Account Crediting</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Container>
    );
};

export default DiwaliReporting;